import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import Wordmark from "@/components/Wordmark";
import CTAButton from "@/components/CTAButton";

const navItems = [
  { label: "Home", to: "/", mobileOnly: true },
  { label: "Our Meals", to: "/meals" },
  { label: "Meal Plans", to: "/plans" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Our Standards", to: "/food-standards" },
  { label: "Our Story", to: "/our-story" },
  { label: "FAQs", to: "/faqs" },
];

export const AnnouncementBar = () => {
  const brand = useBrand();
  const messages = brand.announcement.messages || [];
  const [i, setI] = useState(0);

  useEffect(() => {
    if (messages.length < 2) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % messages.length), 5200);
    return () => clearInterval(id);
  }, [messages.length]);

  if (!brand.announcement.enabled || messages.length === 0) return null;

  return (
    <div data-testid="announcement-bar" className="relative z-[60] bg-coralInk text-white">
      <div className="mbm-container flex min-h-[56px] flex-wrap items-center justify-center gap-x-4 gap-y-1 py-2.5 text-center sm:min-h-[58px]">
        <p
          key={i}
          data-testid="announcement-message"
          className="animate-in fade-in slide-in-from-bottom-1 text-[14px] font-extrabold tracking-[0.01em] duration-500 sm:text-[15.5px]"
        >
          {messages[i]}
        </p>
        <Link
          to={brand.announcement.ctaHref}
          data-testid="announcement-cta"
          className="group inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-amber px-5 text-[13px] font-extrabold text-cocoa shadow-[0_6px_16px_-8px_rgba(0,0,0,0.55)] transition-transform duration-200 hover:-translate-y-0.5 sm:text-sm"
        >
          {brand.announcement.ctaLabel}
          <ChevronRight
            className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Link>
      </div>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50 pt-3 sm:pt-4">
      <div className="mbm-container">
        <div className="flex items-center justify-between gap-4 rounded-[30px] bg-surface px-4 py-3 shadow-softLg ring-1 ring-cocoa/5 sm:px-6 sm:py-3.5">
          <Wordmark size="lg" />

        <nav aria-label="Main" className="hidden items-center gap-0.5 xl:flex">
          {navItems
            .filter((item) => !item.mobileOnly)
            .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-2.5 text-[14px] font-extrabold transition-colors xl:px-4 ${
                  isActive
                    ? "bg-amber text-cocoa"
                    : "text-cocoa hover:bg-sageSoft"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 xl:flex">
          <Link
            to={user ? "/account" : "/sign-in"}
            data-testid="header-sign-in"
            className="whitespace-nowrap rounded-full px-3 py-2.5 text-[14px] font-extrabold text-cocoa transition-colors hover:bg-sageSoft"
          >
            {user ? "My Account" : "Sign In"}
          </Link>
          <CTAButton to="/get-started" data-testid="header-get-started" className="!min-h-[48px] whitespace-nowrap !px-5 !text-[14px] xl:!px-6">
            Get Started
          </CTAButton>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-amber text-cocoa shadow-soft xl:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="mbm-container mt-2 xl:hidden">
          <nav
            aria-label="Mobile"
            className="flex flex-col rounded-[26px] bg-surface px-5 py-3 shadow-softLg"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `border-b border-line py-3.5 text-base font-bold ${
                    isActive ? "text-coralInk" : "text-cocoa"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-3 pt-4">
              <CTAButton to="/get-started" data-testid="mobile-get-started">
                Get Started
              </CTAButton>
              <CTAButton to={user ? "/account" : "/sign-in"} variant="outline" data-testid="mobile-sign-in">
                {user ? "My Account" : "Sign In"}
              </CTAButton>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
